import React, { useState, useEffect } from "react";

export default function DCFInputs({ fcfHistory = [], histMultiple = 0, onChange }) {
    // Helpers
    const formatCr = num => `${Math.round(num).toLocaleString()} Cr`;
    const formatPct = num => `${(num * 100).toFixed(1)}%`;

    // 1️⃣ Base Free Cash Flow
    const [fcfMode, setFcfMode] = useState("current");
    const [customFcf, setCustomFcf] = useState("");
    const rollingAvg = n => {
        const slice = fcfHistory.slice(-n);
        return slice.length < n ? 0 : slice.reduce((s, v) => s + v, 0) / n;
    };
    const fcfValues = {
        current: fcfHistory.at(-1) || 0,
        "3yr": rollingAvg(3),
        "5yr": rollingAvg(5),
        "7yr": rollingAvg(7),
        "10yr": rollingAvg(10),
        custom: parseFloat(customFcf) || 0,
    };
    const fcfOptionKeys = ["current", "3yr", "5yr", "7yr", "10yr", "custom"];
    const fcfLabelMap = {
        current: "Current FCF",
        "3yr": "3-Year Avg FCF",
        "5yr": "5-Year Avg FCF",
        "7yr": "7-Year Avg FCF",
        "10yr": "10-Year Avg FCF",
    };
    const fcfOptions = fcfOptionKeys.map(key => ({
        value: key,
        label: key === "custom"
            ? "Custom"
            : `${fcfLabelMap[key]} (${formatCr(fcfValues[key])})`,
    }));
    const selectedFcf = fcfValues[fcfMode];

    // 2️⃣ Growth Rate
    const [growMode, setGrowMode] = useState("5yr");
    const [customGrow, setCustomGrow] = useState(0.10);
    function computeCAGR(start, end, yrs) {
        return start > 0 ? Math.pow(end / start, 1 / yrs) - 1 : 0;
    }
    const n = fcfHistory.length;
    const cagrValues = {
        "3yr": computeCAGR(fcfHistory[n - 4], fcfHistory[n - 1], 3),
        "5yr": computeCAGR(fcfHistory[n - 6], fcfHistory[n - 1], 5),
        "7yr": computeCAGR(fcfHistory[n - 8], fcfHistory[n - 1], 7),
        "10yr": computeCAGR(fcfHistory[n - 11], fcfHistory[n - 1], 10),
        custom: customGrow,
    };
    const growKeys = ["3yr", "5yr", "7yr", "10yr", "custom"];
    const growLabelMap = {
        "3yr": "3-Yr CAGR",
        "5yr": "5-Yr CAGR",
        "7yr": "7-Yr CAGR",
        "10yr": "10-Yr CAGR",
    };
    const growOptions = growKeys.map(key => ({
        value: key,
        label: key === "custom"
            ? "Custom"
            : `${growLabelMap[key]} (${formatPct(cagrValues[key])})`,
    }));
    const selectedGrow = cagrValues[growMode];

    // 3️⃣ Discount Rate
    const [discountRate, setDiscountRate] = useState(0.12);

    // 4️⃣ Projection Years
    const [projectionYears, setProjectionYears] = useState(10);

    // 5️⃣ Terminal Multiple
    const [termMode, setTermMode] = useState("5yr");
    const [customTerm, setCustomTerm] = useState("");
    const selectedTermMultiple = termMode === "5yr"
        ? histMultiple
        : parseFloat(customTerm) || 0;
    const termOptions = [
        { value: "5yr", label: `5-Yr Avg FCF Multiple (${histMultiple.toFixed(1)}×)` },
        { value: "custom", label: "Custom" },
    ];

    // 6️⃣ Margin of Safety
    const [marginOfSafety, setMarginOfSafety] = useState(0.20);

    // Propagate changes
    useEffect(() => {
        onChange({
            currentFCF: selectedFcf,
            growthRate: selectedGrow,
            discountRate,
            projectionYears,
            terminalMultiple: selectedTermMultiple,
            marginOfSafety,
        });
    }, [selectedFcf, selectedGrow, discountRate, projectionYears, selectedTermMultiple, marginOfSafety, onChange]);

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem"
        }}>
            {/* Base FCF */}
            <div style={{ marginBottom: "0.7rem", display: "flex", alignItems: "center", gap: "1.2rem" }}>

                <label style={{ fontWeight: 500, fontSize: "14px", minWidth: 150 }}>
                    Base Free Cash Flow
                </label>
                <select
                    value={fcfMode}
                    onChange={e => setFcfMode(e.target.value)}
                    style={{
                        width: "80%",
                        minWidth: 180,
                        maxWidth: 260,
                        marginTop: "0.7rem",
                        fontSize: "14px",
                        padding: "8px"
                    }}
                >
                    {fcfOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>

                {fcfMode === "custom" && (
                    <input type="number" placeholder="Enter custom FCF" value={customFcf}
                        onChange={e => setCustomFcf(e.target.value)}
                        style={{ marginTop: "0.3rem", width: "60%", minWidth: 120, maxWidth: 180, fontSize: "14px", padding: "6px" }} />
                )}
            </div>

            {/* Growth Rate */}
            <div style={{ marginBottom: "0.7rem", display: "flex", alignItems: "center", gap: "1.2rem" }}>
                <label style={{ fontWeight: 500, fontSize: "14px", minWidth: 150 }}>Growth Rate</label>
                <select value={growMode} onChange={e => setGrowMode(e.target.value)}
                    style={{
                        width: "80%",
                        minWidth: 180,
                        maxWidth: 260,
                        fontSize: "14px",
                        padding: "8px"
                    }}>
                    {growOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {growMode === "custom" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.3rem" }}>
                        <span style={{ width: "48px", textAlign: "right", fontSize: "13px" }}>{formatPct(customGrow)}</span>
                        <input type="range" min={0} max={0.3} step={0.001} value={customGrow}
                            onChange={e => setCustomGrow(Number(e.target.value))}
                            style={{ flex: 1, minWidth: 60, maxWidth: 120 }} />
                    </div>
                )}
            </div>


            <div style={{ marginBottom: "0.7rem", display: "flex", alignItems: "center", gap: "1.2rem" }}>
                <label style={{ fontWeight: 500, fontSize: "14px", minWidth: 150 }}>Discount Rate</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "80%" }}>
                    <span style={{ width: "48px", textAlign: "right", fontSize: "13px" }}>{formatPct(discountRate)}</span>
                    <input
                        type="range"
                        min={0}
                        max={0.3}
                        step={0.001}
                        value={discountRate}
                        onChange={e => setDiscountRate(Number(e.target.value))}
                        style={{ flex: 1, minWidth: 60, maxWidth: 120 }}
                    />
                </div>
            </div>

            <div style={{ marginBottom: "0.7rem", display: "flex", alignItems: "center", gap: "1.2rem" }}>
                <label style={{ fontWeight: 500, fontSize: "14px", minWidth: 150 }}>Projection Years</label>
                <select
                    value={projectionYears}
                    onChange={e => setProjectionYears(Number(e.target.value))}
                    style={{
                        width: "80%",
                        minWidth: 180,
                        maxWidth: 260,
                        fontSize: "14px",
                        padding: "8px"
                    }}
                >
                    {[5, 10, 15].map(y => <option key={y} value={y}>{y} Years</option>)}
                </select>
            </div>

            <div style={{ marginBottom: "0.7rem", display: "flex", alignItems: "center", gap: "1.2rem" }}>
                <label style={{ fontWeight: 500, fontSize: "14px", minWidth: 150 }}>Terminal FCF Multiple</label>
                <select
                    value={termMode}
                    onChange={e => setTermMode(e.target.value)}
                    style={{
                        width: "80%",
                        minWidth: 180,
                        maxWidth: 260,
                        fontSize: "14px",
                        padding: "8px"
                    }}
                >
                    {termOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {termMode === "custom" && (
                    <input
                        type="number"
                        placeholder="Enter custom multiple"
                        value={customTerm}
                        onChange={e => setCustomTerm(e.target.value)}
                        style={{ marginLeft: "1rem", width: "60%", minWidth: 120, maxWidth: 180, fontSize: "14px", padding: "6px" }}
                    />
                )}
            </div>

            <div style={{ marginBottom: "0.7rem", display: "flex", alignItems: "center", gap: "1.2rem" }}>
                <label style={{ fontWeight: 500, fontSize: "14px", minWidth: 150 }}>Margin of Safety</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "80%" }}>
                    <span style={{ width: "48px", textAlign: "right", fontSize: "13px" }}>{formatPct(marginOfSafety)}</span>
                    <input
                        type="range"
                        min={0}
                        max={0.5}
                        step={0.01}
                        value={marginOfSafety}
                        onChange={e => setMarginOfSafety(Number(e.target.value))}
                        style={{ flex: 1, minWidth: 60, maxWidth: 120 }}
                    />
                </div>
            </div>


        </div>
    );
}
